import { useRef } from 'react';
import {
  usePortal,
  useAnimationState,
  useBodyScrollLock,
  useEscapeKey,
  useFocusTrap,
  useInitialFocus,
  useClickOutside,
} from '@/shared/hooks';

interface UseModalOptions {
  isOpen: boolean;
  onClose: () => void;
  disableEscapeClose?: boolean;
  disableOutsideTab?: boolean;
  disableOutsideClick?: boolean;
}

interface UseModalReturn {
  isVisible: boolean;
  animateIn: boolean;
  container: HTMLDivElement | null;
  modalRef: React.RefObject<HTMLDivElement>;
}

export const useModal = ({
  isOpen,
  onClose,
  disableEscapeClose = false,
  disableOutsideTab = false,
  disableOutsideClick = false,
}: UseModalOptions): UseModalReturn => {
  // Hooks reutilizables
  const container = usePortal();
  const { isVisible, isAnimating: animateIn } = useAnimationState(isOpen, { exitDelay: 200 });
  const modalRef = useRef<HTMLDivElement>(null);

  // Bloquear scroll del body
  useBodyScrollLock(isOpen);

  // Manejar tecla Escape
  useEscapeKey(isOpen, onClose, disableEscapeClose);

  // Focus inicial
  useInitialFocus(isOpen, modalRef);

  // Focus trap
  useFocusTrap(isOpen, modalRef, !disableOutsideTab);

  // Manejar clic fuera del modal
  useClickOutside(isOpen, modalRef, onClose, disableOutsideClick);

  return {
    isVisible,
    animateIn,
    container,
    modalRef,
  };
};
